import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCardOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import SettingsIcon from "@mui/icons-material/SettingsRounded";
import KeyIcon from "@mui/icons-material/VpnKeyOutlined";
import { Box, Stack, Typography } from "@mui/material";
import { useAuthStore } from "@stores/authStore";
import {
  Outlet,
  Link as RouterLink,
  useRouterState,
} from "@tanstack/react-router";
import type { ElementType } from "react";

interface NavItem {
  label: string;
  to: string;
  icon: ElementType;
  /** Route prefix for active highlighting; defaults to `to`. Lets the
   *  org-detail page light up the parent "Organisations" item. */
  matchPrefix?: string;
}

const NAV: NavItem[] = [
  { label: "Profile", to: "/settings/profile", icon: PersonIcon },
  {
    label: "Organisations",
    to: "/settings/organisations",
    icon: BusinessIcon,
    matchPrefix: "/settings/organisations",
  },
  { label: "Billing", to: "/settings/billing", icon: CreditCardIcon },
  { label: "API keys", to: "/settings/api-keys", icon: KeyIcon },
];

/**
 * Settings shell — sub-nav rail + outlet, mounted inside `AppLayout`.
 *
 * Standard web-app pattern: the global Sidebar and Header stay visible
 * (you leave settings the same way you leave any other page — by
 * clicking somewhere else in the app). No overlay, no ESC hijack, no
 * "Back to workspace" gesture. The previous design was a full-viewport
 * overlay whose Back button walked router history one step at a time,
 * trapping users inside `/settings/*` until they clicked Back enough
 * times to escape.
 */
export function SettingsLayout() {
  const user = useAuthStore((s) => s.user);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <Box sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <Box
        component="nav"
        aria-label="Settings sections"
        sx={{
          width: 240,
          flexShrink: 0,
          borderRight: "1px solid",
          borderColor: "divider",
          bgcolor: "surface.sunken",
          py: 3,
          px: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{ px: 1.5, mb: 2 }}
        >
          <SettingsIcon sx={{ color: "primary.main", fontSize: 24 }} />
          <Box>
            <Typography
              sx={{
                fontFamily: (t) => t.fonts.mono,
                fontSize: "0.62rem",
                color: "text.disabled",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontWeight: 600,
                lineHeight: 1,
              }}
            >
              Account
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                color: "text.primary",
                lineHeight: 1.2,
              }}
            >
              Settings
            </Typography>
          </Box>
        </Stack>

        {NAV.map((item) => {
          const Icon = item.icon;
          const active = pathname.startsWith(item.matchPrefix ?? item.to);
          return (
            <Box
              key={item.to}
              component={RouterLink}
              to={item.to}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 1.5,
                py: 1,
                borderRadius: 1.25,
                color: active ? "primary.light" : "text.secondary",
                bgcolor: active ? "accent.soft" : "transparent",
                textDecoration: "none",
                fontSize: "0.875rem",
                fontWeight: 600,
                border: "1px solid",
                borderColor: active ? "border.accent" : "transparent",
                transition: (t) =>
                  `background-color ${t.motion.fast}, color ${t.motion.fast}`,
                "&:hover": {
                  color: active ? "primary.light" : "text.primary",
                  bgcolor: active ? "accent.soft" : "surface.raised",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
              {item.label}
            </Box>
          );
        })}

        <Box sx={{ flexGrow: 1 }} />

        {user?.email && (
          <Typography
            variant="caption"
            sx={{
              color: "text.disabled",
              px: 1.5,
              mt: 2,
              display: "block",
              lineHeight: 1.4,
            }}
          >
            Signed in as{" "}
            <Box
              component="span"
              sx={{ color: "text.secondary", fontWeight: 600 }}
            >
              {user.email}
            </Box>
          </Typography>
        )}
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          overflowY: "auto",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
